import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import {
  isAdmin,
  type BrandAssetKind,
  type InstitutionLocationDto,
  type InstitutionSettingsDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../lib/i18n';

/** Definições → Configuração Global (dados da instituição, locais, branding). */
export function SettingsGlobal() {
  const t = useT();
  const { user } = useAuth();
  const admin = isAdmin(user?.roles ?? []);
  const [settings, setSettings] = useState<InstitutionSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!admin) {
      setLoading(false);
      return;
    }
    api
      .institutionSettings()
      .then(setSettings)
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [admin]);

  if (!admin) {
    return (
      <section>
        <div className="page-head">
          <div className="page-head-text">
            <h2>{t('settings.global.title')}</h2>
          </div>
        </div>
        <p className="muted">{t('settings.global.adminOnly')}</p>
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('settings.global.title')}</h2>
          <p className="page-sub">{t('settings.global.subtitle')}</p>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}
      {loading || !settings ? (
        <p className="muted">{t('common.loading')}</p>
      ) : (
        <>
          <InstitutionForm settings={settings} onSaved={setSettings} onError={setError} />
          <Branding settings={settings} onChange={setSettings} onError={setError} />
          <Locations
            locations={settings.locations}
            onChange={(locations) => setSettings({ ...settings, locations })}
            onError={setError}
          />
        </>
      )}
    </section>
  );
}

function InstitutionForm({
  settings,
  onSaved,
  onError,
}: {
  settings: InstitutionSettingsDto;
  onSaved: (s: InstitutionSettingsDto) => void;
  onError: (e: string | null) => void;
}) {
  const t = useT();
  const [name, setName] = useState(settings.name ?? '');
  const [nif, setNif] = useState(settings.nif ?? '');
  const [phone, setPhone] = useState(settings.phone ?? '');
  const [mobile, setMobile] = useState(settings.mobile ?? '');
  const [email, setEmail] = useState(settings.email ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setSaved(false);
    onError(null);
    try {
      onSaved(await api.updateInstitution({ name, nif, phone, mobile, email }));
      setSaved(true);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="erp-panel parking-form" onSubmit={save} style={{ marginBottom: 16 }}>
      <div className="erp-toolbar">
        <strong>{t('settings.global.institution')}</strong>
      </div>
      <div className="parking-grid">
        <label className="builder-field builder-wide">
          <span>{t('settings.global.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="builder-field">
          <span>{t('settings.global.nif')}</span>
          <input value={nif} onChange={(e) => setNif(e.target.value)} />
        </label>
        <label className="builder-field">
          <span>{t('settings.global.phone')}</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </label>
        <label className="builder-field">
          <span>{t('settings.global.mobile')}</span>
          <input value={mobile} onChange={(e) => setMobile(e.target.value)} />
        </label>
        <label className="builder-field">
          <span>{t('settings.global.email')}</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
      </div>
      <div className="builder-actions">
        <div className="spacer" style={{ flex: 1 }} />
        {saved && <span className="page-sub">{t('settings.common.saved')}</span>}
        <button type="submit" disabled={busy}>
          {busy ? t('settings.common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  );
}

const ASSETS: { kind: BrandAssetKind; labelKey: string; hintKey: string }[] = [
  {
    kind: 'logo',
    labelKey: 'settings.global.asset.logo.label',
    hintKey: 'settings.global.asset.logo.hint',
  },
  {
    kind: 'icon',
    labelKey: 'settings.global.asset.icon.label',
    hintKey: 'settings.global.asset.icon.hint',
  },
  {
    kind: 'loginBackground',
    labelKey: 'settings.global.asset.loginBackground.label',
    hintKey: 'settings.global.asset.loginBackground.hint',
  },
];

function Branding({
  settings,
  onChange,
  onError,
}: {
  settings: InstitutionSettingsDto;
  onChange: (s: InstitutionSettingsDto) => void;
  onError: (e: string | null) => void;
}) {
  const t = useT();
  return (
    <div className="erp-panel" style={{ marginBottom: 16 }}>
      <div className="erp-toolbar">
        <strong>{t('settings.global.branding')}</strong>
      </div>
      <div className="brand-assets">
        {ASSETS.map((a) => (
          <AssetSlot
            key={a.kind}
            kind={a.kind}
            label={t(a.labelKey)}
            hint={t(a.hintKey)}
            value={settings[a.kind] ?? null}
            onChange={onChange}
            onError={onError}
          />
        ))}
      </div>
    </div>
  );
}

function AssetSlot({
  kind,
  label,
  hint,
  value,
  onChange,
  onError,
}: {
  kind: BrandAssetKind;
  label: string;
  hint: string;
  value: string | null;
  onChange: (s: InstitutionSettingsDto) => void;
  onError: (e: string | null) => void;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function pick(file: File) {
    if (file.size > 4 * 1024 * 1024) {
      onError(t('settings.global.imageTooLarge'));
      return;
    }
    onError(null);
    setBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        onChange(await api.setInstitutionAsset(kind, reader.result as string));
      } catch (err) {
        onError((err as Error).message);
      } finally {
        setBusy(false);
      }
    };
    reader.onerror = () => {
      onError(t('settings.global.imageReadError'));
      setBusy(false);
    };
    reader.readAsDataURL(file);
  }

  async function clear() {
    setBusy(true);
    try {
      onChange(await api.setInstitutionAsset(kind, null));
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="brand-slot">
      <div className="brand-preview">
        {value ? (
          <img src={value} alt={label} />
        ) : (
          <span className="muted">{t('settings.global.noImage')}</span>
        )}
      </div>
      <div className="brand-meta">
        <strong>{label}</strong>
        <span className="page-sub">{hint}</span>
        <div className="actions-row" style={{ gap: 8, marginTop: 6 }}>
          <button
            type="button"
            className="secondary"
            onClick={() => input.current?.click()}
            disabled={busy}
          >
            {busy ? '…' : t('settings.global.upload')}
          </button>
          {value && (
            <button
              type="button"
              className="btn-danger"
              onClick={() => void clear()}
              disabled={busy}
            >
              {t('common.remove')}
            </button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void pick(f);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

function Locations({
  locations,
  onChange,
  onError,
}: {
  locations: InstitutionLocationDto[];
  onChange: (l: InstitutionLocationDto[]) => void;
  onError: (e: string | null) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [abbr, setAbbr] = useState('');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    onError(null);
    try {
      const created = await api.createInstitutionLocation({
        name,
        abbreviation: abbr || null,
        address: address || null,
      });
      onChange([...locations, created]);
      setName('');
      setAbbr('');
      setAddress('');
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('settings.global.deleteLocationConfirm'))) return;
    onError(null);
    try {
      await api.deleteInstitutionLocation(id);
      onChange(locations.filter((l) => l.id !== id));
    } catch (err) {
      onError((err as Error).message);
    }
  }

  return (
    <div className="erp-panel">
      <div className="erp-toolbar">
        <strong>{t('settings.global.locations')}</strong>
        <span className="page-sub">{locations.length}</span>
      </div>
      <form className="parking-grid" onSubmit={add} style={{ alignItems: 'end' }}>
        <label className="builder-field">
          <span>{t('settings.global.locName')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('settings.global.locNamePlaceholder')}
          />
        </label>
        <label className="builder-field">
          <span>{t('settings.global.locAbbr')}</span>
          <input
            value={abbr}
            onChange={(e) => setAbbr(e.target.value)}
            placeholder={t('settings.global.locAbbrPlaceholder')}
          />
        </label>
        <label className="builder-field builder-wide">
          <span>{t('settings.global.locAddress')}</span>
          <input value={address} onChange={(e) => setAddress(e.target.value)} />
        </label>
        <div className="builder-field">
          <span>&nbsp;</span>
          <button type="submit" disabled={busy || !name.trim()}>
            {t('common.add')}
          </button>
        </div>
      </form>
      {locations.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="grid">
            <thead>
              <tr>
                <th>{t('settings.global.colName')}</th>
                <th>{t('settings.global.colAbbr')}</th>
                <th>{t('settings.global.colAddress')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {locations.map((l) => (
                <tr key={l.id}>
                  <td>{l.name}</td>
                  <td>{l.abbreviation ?? '—'}</td>
                  <td>{l.address ?? '—'}</td>
                  <td>
                    <button type="button" className="btn-danger" onClick={() => void remove(l.id)}>
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
